// src/components/property/PromptTemplateSelect.tsx
/**
 * PromptTemplateSelect Component
 * Dropdown for selecting video generation prompt templates
 * Filters out user-excluded templates and includes user's custom templates
 *
 * Feature: Property Video Prompt Templates
 */

import { memo, useMemo } from 'react';
import { useQuery } from 'convex/react';
import { api } from '../../../convex/_generated/api';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type {
  IPromptTemplateSelectProps,
  IPromptTemplate,
  PromptTemplateId,
} from '@/@types/promptTemplates';
import type { IUserPromptTemplate, TemplateCategory } from '@/@types/videoSettings';

// Display template type that can be either system or user template
interface IDisplayTemplate {
  _id: PromptTemplateId;
  name: string;
  isUserTemplate?: boolean;
}

const RANDOM_VALUE = '__random__';

// Helper function to map image type to category
function getImageTypeCategory(imageType: string): string {
  if (imageType === 'building') return 'building';
  if (['backyard', 'pool', 'garden', 'parking', 'garage', 'balcony'].includes(imageType))
    return 'outdoor';
  if (['living_room', 'kitchen', 'bedroom', 'bathroom'].includes(imageType)) return 'interior';
  return 'other';
}

function PromptTemplateSelect({
  value,
  onChange,
  imageType,
  disabled = false,
  triggerId,
}: IPromptTemplateSelectProps) {
  const templates = useQuery(api.property.promptTemplates.listByImageType, {
    imageType,
  });
  const preferences = useQuery(api.property.userTemplatePreferences.get);
  const userTemplates = useQuery(api.property.userPromptTemplates.list);

  // Filter out excluded templates and add user templates
  const availableTemplates = useMemo((): IDisplayTemplate[] => {
    if (!templates) return [];

    const excludedIds = preferences?.excludedTemplateIds ?? [];
    const filtered: IDisplayTemplate[] = templates
      .filter((t: IPromptTemplate) => !excludedIds.includes(t._id))
      .map((t: IPromptTemplate) => ({ _id: t._id, name: t.name, isUserTemplate: false }));

    // Add user templates that match this image type's category
    const category = getImageTypeCategory(imageType) as TemplateCategory;
    const matchingUserTemplates: IDisplayTemplate[] = (userTemplates ?? [])
      .filter((t: IUserPromptTemplate) => t.categories.includes(category))
      .map((t: IUserPromptTemplate) => ({
        _id: t._id,
        name: t.name,
        isUserTemplate: true,
      }));

    return [...matchingUserTemplates, ...filtered];
  }, [templates, preferences?.excludedTemplateIds, userTemplates, imageType]);

  const handleChange = (selectedValue: string) => {
    if (selectedValue === RANDOM_VALUE) {
      onChange(null);
    } else {
      onChange(selectedValue as PromptTemplateId);
    }
  };

  const currentValue = value ?? RANDOM_VALUE;

  return (
    <Select value={currentValue} onValueChange={handleChange} disabled={disabled || !templates}>
      <SelectTrigger id={triggerId} className="w-full">
        <SelectValue placeholder="Select style..." />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={RANDOM_VALUE}>
          <span className="flex items-center gap-2">
            <span>🎲</span>
            <span>Random</span>
          </span>
        </SelectItem>
        {availableTemplates.map(template => (
          <SelectItem key={template._id} value={template._id}>
            <span className="flex items-center gap-2">
              {(template as { isUserTemplate?: boolean }).isUserTemplate && (
                <span className="text-xs bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 px-1.5 py-0.5 rounded mr-1">
                  Custom
                </span>
              )}
              {template.name}
            </span>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

export const PromptTemplateSelectMemo = memo(PromptTemplateSelect);
